/**
 * The PictureUp marks (brand kit v1, locked 15 Oct 2026; Figma
 * GlnntNB4AUBdMfuyBW0jgP is the source of truth).
 *
 *   <PictureUpSymbol />      four crop-mark brackets framing an up-pointing
 *                            play triangle. No tile: the rounded tile is
 *                            for the app icon / favicon only.
 *   <PictureUpLockup />      symbol + "PictureUp" wordmark (Geist SemiBold,
 *                            -4%, outlined paths so CSS can never set it in
 *                            caps or another font).
 *   <PictureUpSymbolLoop />  the PictureUp_Symbol_Loop: a seamless 2s loop,
 *                            brackets breathe, triangle bobs. The loading
 *                            and processing state on PictureUp surfaces.
 *
 * Colour: brackets and wordmark ride on currentColor (paper on ink, ink on
 * paper); the triangle is brand blue, or currentColor with `mono` (the
 * "on blue, everything goes ink" rule). Never recolour the brackets,
 * outline the mark, or add rings or gradients.
 */
import {
  PU_BRACKETS,
  PU_LOCKUP_H,
  PU_LOCKUP_W,
  PU_SYMBOL_SCALE,
  PU_TRIANGLE,
  PU_WORDMARK_D,
} from '@/lib/pictureup-geometry';

export const PU_BLUE = '#7FA0C3';

function SymbolPaths({ mono }: { mono?: boolean }) {
  const tri = mono ? 'currentColor' : PU_BLUE;
  return (
    <>
      {PU_BRACKETS.map((d) => (
        <path key={d} d={d} stroke="currentColor" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      ))}
      <path d={PU_TRIANGLE} fill={tri} stroke={tri} strokeWidth={6} strokeLinejoin="round" />
    </>
  );
}

export function PictureUpSymbol({
  size = 24,
  mono,
  className,
  title,
}: {
  size?: number;
  mono?: boolean;
  className?: string;
  /** Accessible name; omit when a visible label sits next to it. */
  title?: string;
}) {
  return (
    <svg
      viewBox="0 0 96 96"
      width={size}
      height={size}
      fill="none"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <SymbolPaths mono={mono} />
    </svg>
  );
}

/**
 * The primary lockup. `height` is the rendered height in px (the Figma
 * lockup is 283 x 66); the symbol sits tight to the wordmark exactly as
 * locked. Below 96px wide the kit says use the symbol alone.
 */
export function PictureUpLockup({
  height = 22,
  mono,
  className,
  label = 'PictureUp',
}: {
  height?: number;
  mono?: boolean;
  className?: string;
  label?: string;
}) {
  const width = (height * PU_LOCKUP_W) / PU_LOCKUP_H;
  return (
    <svg
      viewBox={`0 0 ${PU_LOCKUP_W} ${PU_LOCKUP_H}`}
      width={width}
      height={height}
      fill="none"
      className={className}
      role="img"
      aria-label={label}
      // A fixed brand mark: never mirrors in RTL locales.
      direction="ltr"
    >
      <g transform={`scale(${PU_SYMBOL_SCALE})`}>
        <SymbolPaths mono={mono} />
      </g>
      <path d={PU_WORDMARK_D} fill="currentColor" />
    </svg>
  );
}

/**
 * PictureUp_Symbol_Loop as SVG + CSS (the MP4 / ProRes renders are for
 * film; a page needs something light). 2s, seamless: the brackets breathe
 * out and back from their corners, the triangle bobs up and settles.
 * prefers-reduced-motion holds it still. Styles live in pictureup.css.
 */
export function PictureUpSymbolLoop({ size = 64, className }: { size?: number; className?: string }) {
  const corners: Array<[string, string]> = [
    [PU_BRACKETS[0], 'pu-loop-br pu-loop-tl'],
    [PU_BRACKETS[1], 'pu-loop-br pu-loop-tr'],
    [PU_BRACKETS[2], 'pu-loop-br pu-loop-bl'],
    [PU_BRACKETS[3], 'pu-loop-br pu-loop-brr'],
  ];
  return (
    <svg viewBox="0 0 96 96" width={size} height={size} fill="none" className={`pu-loop ${className ?? ''}`} aria-hidden>
      {corners.map(([d, cls]) => (
        <path key={d} d={d} className={cls} stroke="currentColor" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      ))}
      <path
        d={PU_TRIANGLE}
        className="pu-loop-tri"
        fill={PU_BLUE}
        stroke={PU_BLUE}
        strokeWidth={6}
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The browser-tab / home-screen icons for PictureUp pages. */
export const PU_ICONS = {
  favicon: '/brand/pictureup/pictureup-app-icon.svg',
  favicon32: '/brand/pictureup/pictureup-favicon-32.png',
  touch: '/brand/pictureup/pictureup-touch-180.png',
  manifest: '/brand/pictureup/site.webmanifest',
} as const;
